import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Video,
  ArrowRight,
  Quote,
  Compass,
  TrendingUp,
  Sparkles,
  ExternalLink,
  Mic,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import PortalLayout, {
  getMemberName,
  setMemberName,
} from '../components/portal/PortalLayout';
import {
  QUICK_LINKS,
  NEXT_MEETING,
  getTodayReflection,
  getGreeting,
  daysUntilRetreat,
  daysUntil2027,
  getSpeakerForMeeting,
  RESET_2026_PHOTOS,
} from '../data/portalData';

// Rotate through Reset 2026 photos every N seconds
function useRotatingPhoto(intervalMs = 6000) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % RESET_2026_PHOTOS.length),
      intervalMs,
    );
    return () => clearInterval(t);
  }, [intervalMs]);
  return RESET_2026_PHOTOS[idx];
}

function NameGate({ onSaved }) {
  const [value, setValue] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setMemberName(value.trim());
    onSaved(value.trim());
  };
  return (
    <PortalLayout>
      <div
        className="min-h-[80vh] flex items-center justify-center px-6"
        data-testid="portal-name-gate"
      >
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-[#111111] border border-white/10 rounded-2xl p-8 md:p-10 text-center"
        >
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-4">
            Welcome, brother
          </p>
          <h1 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight mb-3">
            First, your <span className="italic text-[#D4AF37]">name.</span>
          </h1>
          <p className="text-zinc-400 font-manrope text-sm mb-8">
            So the portal can meet you where you are.
          </p>
          <Input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Your first name"
            className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12 text-center mb-4"
            data-testid="name-gate-input"
            autoFocus
          />
          <Button
            type="submit"
            disabled={!value.trim()}
            className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50"
            data-testid="name-gate-submit"
          >
            Enter the Portal
          </Button>
        </form>
      </div>
    </PortalLayout>
  );
}

export default function PortalHome() {
  const [name, setName] = useState('');
  const [reflection] = useState(getTodayReflection());
  const [days] = useState(daysUntilRetreat());
  const [days2027] = useState(daysUntil2027());
  const retreatComplete = days === 0;
  const featuredPhoto = useRotatingPhoto();

  useEffect(() => {
    setName(getMemberName());
  }, []);

  if (!name) return <NameGate onSaved={setName} />;

  const firstName = name.split(' ')[0];

  return (
    <PortalLayout>
      <div className="max-w-7xl mx-auto px-5 py-10">
        {/* Hero */}
        <section className="mb-10" data-testid="portal-hero">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-[11px] font-bold mb-3">
            {getGreeting()}, brother
          </p>
          <h1 className="font-cormorant text-5xl md:text-6xl font-medium text-white leading-[1.05] mb-4">
            {getGreeting()},{' '}
            <span className="italic text-[#D4AF37]">{firstName}.</span>
          </h1>
          <p className="text-zinc-400 font-manrope text-base md:text-lg max-w-2xl leading-relaxed">
            A year-round community where Black men reset their mind, body, purpose, finances,
            and legacy.
          </p>
        </section>

        {/* Three-question row */}
        <section className="grid md:grid-cols-3 gap-4 mb-10" data-testid="portal-three-questions">
          <Link
            to="/portal/reflection"
            className="rounded-2xl border border-white/10 bg-[#111111] p-6 hover:border-[#D4AF37]/50 transition-colors group"
            data-testid="portal-tile-today"
          >
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/25 transition-colors">
              <Compass className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest mb-1">
              What today?
            </p>
            <p className="text-white font-cormorant text-2xl font-medium mb-3">
              Read today&rsquo;s reflection &middot; and act on one line.
            </p>
            <p className="inline-flex items-center gap-1 text-zinc-500 group-hover:text-[#D4AF37] font-manrope text-xs uppercase tracking-widest transition-colors">
              Open the reflection <ArrowRight className="w-3 h-3" />
            </p>
          </Link>
          {(() => {
            const nextSpeaker = getSpeakerForMeeting(NEXT_MEETING.isoDate);
            return (
              <Link
                to="/portal/monthly-meeting"
                className="rounded-2xl border border-white/10 bg-[#111111] hover:border-[#D4AF37]/50 transition-colors group overflow-hidden flex flex-col"
                data-testid="portal-tile-next"
              >
                {/* Rotating Brotherhood Circle photo */}
                <div className="relative h-32 overflow-hidden" data-testid="circle-photo">
                  {RESET_2026_PHOTOS.map((p) => (
                    <img
                      key={p.src}
                      src={p.src}
                      alt="Brothers on the Reset 2026 retreat"
                      loading="lazy"
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                        p.src === featuredPhoto.src ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/95 via-[#111111]/40 to-transparent pointer-events-none" />
                  <p className="absolute bottom-2 left-4 text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.3em] font-bold">
                    Brotherhood Circle
                  </p>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/25 transition-colors">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest mb-1">
                    What&rsquo;s next?
                  </p>
                  <p className="text-white font-cormorant text-2xl font-medium mb-4 leading-tight">
                    {NEXT_MEETING.title} on{' '}
                    {NEXT_MEETING.date.split(',')[0]}.
                  </p>
                  {nextSpeaker && nextSpeaker.confirmed && (
                    <div
                      className="flex items-center gap-3 pt-4 border-t border-white/5"
                      data-testid="portal-tile-next-speaker"
                    >
                      {nextSpeaker.image ? (
                        <img
                          src={nextSpeaker.image}
                          alt={nextSpeaker.name}
                        className="w-11 h-11 rounded-full object-cover border border-[#D4AF37]/40 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                        <Mic className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest mb-0.5">
                        Guest speaker
                      </p>
                      <p className="text-white font-manrope text-sm font-semibold truncate">
                        {nextSpeaker.name}
                      </p>
                    </div>
                  </div>
                )}
                <p className="inline-flex items-center gap-1 text-zinc-500 group-hover:text-[#D4AF37] font-manrope text-xs uppercase tracking-widest transition-colors mt-4">
                  Open the Circle <ArrowRight className="w-3 h-3" />
                </p>
                </div>
              </Link>
            );
          })()}
          <Link
            to="/portal/pillars"
            className="rounded-2xl border border-white/10 bg-[#111111] p-6 hover:border-[#D4AF37]/50 transition-colors group"
            data-testid="portal-tile-pillars"
          >
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/25 transition-colors">
              <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest mb-1">
              How am I growing?
            </p>
            <p className="text-white font-cormorant text-2xl font-medium mb-3">
              Track your reset across the 5 pillars.
            </p>
            <p className="inline-flex items-center gap-1 text-zinc-500 group-hover:text-[#D4AF37] font-manrope text-xs uppercase tracking-widest transition-colors">
              Open the tracker <ArrowRight className="w-3 h-3" />
            </p>
          </Link>
        </section>

        {/* Featured row: Countdown · Next Meeting · Reflection */}
        <section className="grid lg:grid-cols-3 gap-4 mb-12">
          {/* Countdown */}
          <Link
            to="/portal/reset-2027"
            className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/15 to-[#8C6239]/10 border border-[#D4AF37]/30 flex flex-col min-h-[220px] hover:border-[#D4AF37]/60 transition-colors overflow-hidden group"
            data-testid="portal-countdown"
          >
            {/* Rotating Reset 2026 photo */}
            <div className="relative h-32 overflow-hidden" data-testid="countdown-photo">
              {RESET_2026_PHOTOS.map((p) => (
                <img
                  key={p.src}
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                    p.src === featuredPhoto.src ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/95 via-[#0A0A0A]/50 to-transparent pointer-events-none" />
              <p className="absolute bottom-2 left-4 text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.3em] font-bold">
                Reset 2026 &middot; the memory
              </p>
            </div>
            <div className="p-6 flex flex-col justify-between flex-1">
              <div>
                <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.25em] font-bold mb-3">
                  {retreatComplete ? 'The Reset 2027' : 'The Reset 2026'}
                </p>
                {retreatComplete ? (
                  <>
                    <p className="font-cormorant text-6xl font-medium text-white leading-none">
                      {days2027}
                    </p>
                    <p className="text-zinc-300 font-manrope text-sm mt-1">
                      days until Panama
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-cormorant text-6xl font-medium text-white leading-none">
                      {days}
                    </p>
                    <p className="text-zinc-300 font-manrope text-sm mt-1">
                      days until Panama
                    </p>
                  </>
                )}
              </div>
              <div className="mt-4">
                {retreatComplete && (
                  <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest mb-1">
                    2026 &middot; complete <span className="text-[#D4AF37]">&#10003;</span>
                  </p>
                )}
                <p className="text-zinc-500 font-manrope text-xs italic">
                  {retreatComplete
                    ? 'August 12\u201314, 2027 \u00b7 Westin Playa Bonita'
                    : 'July 23\u201326, 2026 \u00b7 The Westin Playa Bonita'}
                </p>
              </div>
            </div>
          </Link>

          {/* Next meeting */}
          <div
            className="rounded-2xl bg-[#111111] border border-white/10 p-6 lg:col-span-1"
            data-testid="portal-next-meeting"
          >
            <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.25em] font-bold mb-3">
              Next Brotherhood Meeting
            </p>
            <h3 className="font-cormorant text-2xl font-medium text-white mb-2">
              {NEXT_MEETING.title}
            </h3>
            <p className="text-zinc-400 font-manrope text-sm italic mb-5">
              {NEXT_MEETING.theme}
            </p>
            <div className="space-y-2 mb-5 text-sm">
              <p className="flex items-center gap-2 text-zinc-300 font-manrope">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                {NEXT_MEETING.date}
              </p>
              <p className="flex items-center gap-2 text-zinc-300 font-manrope">
                <Clock className="w-4 h-4 text-[#D4AF37]" />
                {NEXT_MEETING.time}
              </p>
              <p className="flex items-center gap-2 text-zinc-300 font-manrope">
                <Video className="w-4 h-4 text-[#D4AF37]" />
                Google Meet &middot; link emailed after payment
              </p>
            </div>
            {NEXT_MEETING.concluded ? (
              <p
                className="text-zinc-500 font-manrope text-xs"
                data-testid="portal-meeting-status"
              >
                Series concluded &middot; new rhythm coming soon.
              </p>
            ) : (
              <Link
                to="/portal/monthly-meeting/register"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 rounded-full font-manrope text-sm font-semibold hover:bg-amber-200 transition-colors"
                data-testid="portal-register-btn"
              >
                Register &middot; $25 <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {/* Reflection */}
          <div
            className="rounded-2xl bg-[#111111] border border-white/10 p-6"
            data-testid="portal-reflection"
          >
            <div className="flex items-center gap-2 mb-4">
              <Quote className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.25em] font-bold">
                Today&rsquo;s Reflection
              </p>
            </div>
            <p className="text-white font-cormorant text-xl md:text-2xl italic leading-snug mb-4">
              &ldquo;{reflection.quote}&rdquo;
            </p>
            <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
              &mdash; {reflection.author}
            </p>
          </div>
        </section>

        {/* About the Reset + Founder */}
        <section className="mb-14" data-testid="portal-about">
          <div className="grid lg:grid-cols-5 gap-6">
            {/* What is The Reset */}
            <div
              className="lg:col-span-3 rounded-2xl bg-gradient-to-br from-[#D4AF37]/12 via-[#111111] to-[#111111] border border-[#D4AF37]/25 p-8 md:p-10"
              data-testid="about-what-is-the-reset"
            >
              <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-3">
                About Us
              </p>
              <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight mb-2">
                What is <span className="italic text-[#D4AF37]">The Reset</span>
              </h2>
              <p className="text-zinc-500 font-manrope text-sm uppercase tracking-widest mb-6">
                And why it&rsquo;s needed
              </p>

              <div className="space-y-4 text-zinc-300 font-manrope text-base leading-relaxed">
                <p>
                  The Reset was launched in 2026 as a movement created intentionally for
                  Black men &mdash; a space where we can step away from the noise,
                  expectations, and pressures of everyday life and reconnect with who we
                  are, who we are becoming, and the legacy we are building.
                </p>
                <p>
                  Rooted in our culture, resilience, brotherhood, and collective strength,
                  The Reset provides a judgment-free environment for Black men to lead and
                  design their most productive, purposeful, and fulfilling lives.
                </p>
                <p>
                  But The Reset was never meant to be just an annual retreat; it was
                  created to build an ongoing community where brothers can{' '}
                  <span className="text-white">connect, challenge, affirm, and support</span>{' '}
                  one another throughout the year.
                </p>
                <p>
                  Because when Black men are empowered to thrive &mdash; not simply survive
                  &mdash; the impact reaches beyond the individual to our marriages,
                  families, businesses, neighborhoods, and communities.
                </p>
                <p className="font-cormorant text-2xl md:text-3xl italic text-white leading-snug pt-2 border-t border-white/10">
                  The Reset is about becoming whole, living intentionally, and rising
                  together.
                </p>
              </div>
            </div>

            {/* Founder card */}
            <div
              className="lg:col-span-2 rounded-2xl bg-[#111111] border border-white/10 p-8 md:p-10 flex flex-col"
              data-testid="about-founder"
            >
              <div className="relative -m-8 md:-m-10 mb-6 md:mb-6 rounded-t-2xl overflow-hidden">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/8inhc1di_neil_photo.webp"
                  alt="Neil Alvanzo, founder of The Reset"
                  className="w-full h-56 md:h-64 object-cover object-top"
                  data-testid="founder-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/40 to-transparent pointer-events-none" />
                <div className="absolute top-4 left-4 bg-[#0A0A0A]/85 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded-full">
                  The Founder
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-cormorant text-3xl md:text-4xl font-medium text-white leading-tight">
                    Neil Alvanzo
                  </h3>
                  <p className="text-zinc-300 font-manrope text-xs italic">
                    Founder &middot; ICF-Certified Life &amp; Leadership Coach
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-zinc-300 font-manrope text-sm leading-relaxed">
                <p>
                  ICF-certified life coach and MBA, Neil is an experienced technology
                  executive who has led diverse teams at{' '}
                  <span className="text-white">
                    Salesforce, Booz Allen &amp; Hamilton, and IBM
                  </span>
                  . Trained at the ICF-accredited Ipec Training Institute and a certified
                  practitioner of the Energy Leadership Assessment.
                </p>
                <p>
                  Based in Baltimore, Neil partners with professionals to uncover
                  self-limiting beliefs and build the confidence to lead productive
                  lives, working with clients throughout North America.
                </p>
                <p>
                  He&rsquo;s the founder of The Reset &mdash; a community for Black men
                  focused on designing and leading their most productive lives.
                </p>
                <p className="text-zinc-500 text-xs pt-2 border-t border-white/5">
                  BS, Howard University &middot; MBA, Johns Hopkins &middot; Leadership
                  Development Program alum. Married with two daughters; enjoys bike rides
                  and traveling with family.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick links grid */}
        <section data-testid="portal-quick-links">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-cormorant text-3xl font-medium text-white">
              Quick Links
            </h2>
            <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest">
              Choose your next move
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {QUICK_LINKS.map((l) => {
              const LinkEl = l.external ? 'a' : Link;
              const props = l.external
                ? { href: l.href, target: '_blank', rel: 'noopener noreferrer' }
                : { to: l.href };
              return (
                <LinkEl
                  key={l.key}
                  {...props}
                  className={`group relative rounded-2xl border p-5 transition-all overflow-hidden ${
                    l.featured
                      ? 'bg-gradient-to-br from-[#D4AF37]/15 via-[#111111] to-[#111111] border-[#D4AF37]/40 hover:border-[#D4AF37]/70'
                      : 'bg-[#111111] border-white/10 hover:border-[#D4AF37]/40'
                  }`}
                  data-testid={`quick-link-${l.key}`}
                >
                  {l.featured && (
                    <span className="absolute top-3 right-3 bg-[#D4AF37] text-[#0A0A0A] font-manrope text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full">
                      Now open
                    </span>
                  )}
                  {l.closed && (
                    <span
                      className="absolute top-3 right-3 bg-white/10 text-zinc-400 font-manrope text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border border-white/10"
                      data-testid={`closed-badge-${l.key}`}
                    >
                      Closed
                    </span>
                  )}
                  <div
                    className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${l.color} opacity-[0.12] blur-2xl group-hover:opacity-25 transition-opacity`}
                  />
                  <div className="relative">
                    {l.image && (
                      <img
                        src={l.image}
                        alt={l.imageAlt || l.title}
                        className="w-12 h-12 rounded-full object-cover border border-[#D4AF37]/40 mb-3"
                        data-testid={`quick-link-image-${l.key}`}
                      />
                    )}
                    <h3 className="font-cormorant text-xl font-medium text-white mb-1">
                      {l.title}
                    </h3>
                    <p className="text-zinc-500 font-manrope text-sm">{l.desc}</p>
                    <div className="mt-4 flex items-center gap-1 text-zinc-600 group-hover:text-[#D4AF37] font-manrope text-xs uppercase tracking-widest transition-colors">
                      Open{' '}
                      {l.external ? (
                        <ExternalLink className="w-3 h-3" />
                      ) : (
                        <ArrowRight className="w-3 h-3" />
                      )}
                    </div>
                  </div>
                </LinkEl>
              );
            })}
          </div>
        </section>

        {/* Rotating Guest Speaker Spotlight — after Quick Links */}
        {!NEXT_MEETING.concluded && (() => {
          const nextSpeaker = getSpeakerForMeeting(NEXT_MEETING.isoDate);
          if (!nextSpeaker) return null;
          return (
            <section className="mt-14" data-testid="portal-speaker-spotlight">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-cormorant text-3xl font-medium text-white">
                  Guest Speaker Spotlight
                </h2>
                <p
                  className="text-zinc-500 font-manrope text-xs uppercase tracking-widest"
                  data-testid="speaker-spotlight-month"
                >
                  {nextSpeaker.monthLabel}
                </p>
              </div>
              <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/15 via-[#111111] to-[#111111] p-6 md:p-10">
                <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                  {nextSpeaker.image ? (
                    <img
                      src={nextSpeaker.image}
                      alt={nextSpeaker.name}
                      className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover border border-[#D4AF37]/40 flex-shrink-0"
                      data-testid="speaker-spotlight-photo"
                    />
                  ) : (
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#0A0A0A] border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                      <Mic className="w-10 h-10 text-[#D4AF37]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold mb-2">
                      {nextSpeaker.confirmed ? 'This month\u2019s guest' : 'Guest speaker'}
                    </p>
                    <h3
                      className="font-cormorant text-3xl md:text-4xl font-medium text-white leading-tight mb-1"
                      data-testid="speaker-spotlight-name"
                    >
                      {nextSpeaker.name}
                    </h3>
                    <p className="text-zinc-400 font-manrope text-sm italic mb-1">
                      {nextSpeaker.handle}
                    </p>
                    {nextSpeaker.credentials && (
                      <p
                        className="text-[#D4AF37]/80 font-manrope text-xs tracking-wider mb-3"
                        data-testid="speaker-spotlight-credentials"
                      >
                        {nextSpeaker.credentials}
                      </p>
                    )}
                    {nextSpeaker.role && (
                      <p className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-4">
                        {nextSpeaker.role}
                      </p>
                    )}
                    <p
                      className="text-zinc-300 font-manrope text-base md:text-lg leading-relaxed mb-5 max-w-2xl"
                      data-testid="speaker-spotlight-bio"
                    >
                      {nextSpeaker.bio_long || nextSpeaker.bio}
                    </p>

                    {nextSpeaker.quote && (
                      <div
                        className="relative rounded-xl bg-[#0A0A0A]/60 border-l-2 border-[#D4AF37] px-5 py-4 mb-6 max-w-2xl"
                        data-testid="speaker-spotlight-quote"
                      >
                        <Quote className="absolute -top-2 left-3 w-4 h-4 text-[#D4AF37] bg-[#111111] px-0.5" />
                        <p className="font-cormorant text-white text-xl md:text-2xl italic leading-snug">
                          &ldquo;{nextSpeaker.quote}&rdquo;
                        </p>
                        {nextSpeaker.quote_note && (
                          <p className="text-zinc-500 font-manrope text-xs mt-2">
                            {nextSpeaker.quote_note}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                      {nextSpeaker.confirmed && (
                        <Link
                          to="/portal/monthly-meeting"
                          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-2.5 rounded-full font-manrope text-sm font-semibold hover:bg-amber-200 transition-colors"
                          data-testid="speaker-spotlight-cta"
                        >
                          See the {nextSpeaker.monthLabel.split(' ')[0]} Circle
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      )}
                      {nextSpeaker.href && (
                        <a
                          href={nextSpeaker.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:text-amber-200 transition-colors"
                          data-testid="speaker-spotlight-profile"
                        >
                          View profile <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })()}
      </div>
    </PortalLayout>
  );
}
